// ====== MAP DYNAMICS TIMEZONE CODES → IANA TIMEZONES ======
const dynamicsTzMap = {
  0: "Etc/GMT+12",
  1: "Pacific/Apia",
  2: "Pacific/Honolulu",
  3: "America/Anchorage",
  4: "America/Los_Angeles",
  5: "America/Tijuana",
  6: "Etc/GMT+11",
  7: "America/Adak",
  8: "Pacific/Marquesas",
  9: "Etc/GMT+9",
  10: "America/Denver",
  11: "Etc/GMT+8",
  12: "America/Chihuahua",
  15: "America/Phoenix",
  20: "America/Chicago",
  25: "America/Regina",
  29: "America/Mexico_City",
  33: "America/Guatemala",
  34: "Pacific/Easter",
  35: "America/New_York",
  40: "America/Indianapolis",
  43: "America/Port-au-Prince",
  44: "America/Havana",
  45: "America/Bogota",
  47: "America/Caracas",
  50: "America/Halifax",
  51: "America/Grand_Turk",
  55: "America/La_Paz",
  56: "America/Santiago",
  58: "America/Cuiaba",
  59: "America/Asuncion",
  60: "America/St_Johns",
  65: "America/Sao_Paulo",
  69: "America/Buenos_Aires",
  70: "America/Cayenne",
  71: "America/Bahia",
  72: "America/Miquelon",
  73: "America/Godthab",
  74: "America/Montevideo",
  76: "Etc/GMT+2",
  77: "America/Araguaina",
  80: "Atlantic/Azores",
  83: "Atlantic/Cape_Verde",
  84: "Africa/Casablanca",
  85: "Europe/London",
  90: "Atlantic/Reykjavik",
  92: "Etc/UTC",
  95: "Europe/Budapest",
  100: "Europe/Warsaw",
  105: "Europe/Paris",
  110: "Europe/Berlin",
  113: "Africa/Lagos",
  115: "Europe/Chisinau",
  120: "Africa/Cairo",
  125: "Europe/Kiev",
  129: "Asia/Amman",
  130: "Europe/Bucharest",
  131: "Asia/Beirut",
  133: "Asia/Damascus",
  134: "Europe/Istanbul",
  135: "Asia/Jerusalem",
  140: "Africa/Johannesburg",
  141: "Africa/Windhoek",
  142: "Asia/Hebron",
  145: "Europe/Moscow",
  150: "Asia/Riyadh",
  151: "Europe/Minsk",
  155: "Africa/Nairobi",
  158: "Asia/Baghdad",
  159: "Europe/Kaliningrad",
  160: "Asia/Tehran",
  165: "Asia/Dubai",
  169: "Asia/Baku",
  170: "Asia/Yerevan",
  172: "Indian/Mauritius",
  173: "Asia/Tbilisi",
  174: "Europe/Samara",
  175: "Asia/Kabul",
  176: "Europe/Astrakhan",
  180: "Asia/Yekaterinburg",
  184: "Asia/Karachi",
  185: "Asia/Tashkent",
  190: "Asia/Calcutta",
  193: "Asia/Katmandu",
  195: "Asia/Almaty",
  196: "Asia/Dhaka",
  197: "Asia/Omsk",
  200: "Asia/Colombo",
  201: "Asia/Novosibirsk",
  203: "Asia/Rangoon",
  205: "Asia/Bangkok",
  207: "Asia/Krasnoyarsk",
  208: "Asia/Barnaul",
  209: "Asia/Hovd",
  210: "Asia/Shanghai",
  211: "Asia/Tomsk",
  215: "Asia/Singapore",
  220: "Asia/Taipei",
  225: "Australia/Perth",
  227: "Asia/Irkutsk",
  228: "Asia/Ulaanbaatar",
  229: "Asia/Pyongyang",
  230: "Asia/Seoul",
  231: "Australia/Eucla",
  235: "Asia/Tokyo",
  240: "Asia/Yakutsk",
  241: "Asia/Chita",
  245: "Australia/Darwin",
  250: "Australia/Adelaide",
  255: "Australia/Sydney",
  260: "Australia/Brisbane",
  265: "Australia/Hobart",
  270: "Asia/Vladivostok",
  274: "Australia/Lord_Howe",
  275: "Pacific/Port_Moresby",
  276: "Pacific/Bougainville",
  277: "Pacific/Norfolk",
  278: "Asia/Sakhalin",
  279: "Asia/Srednekolymsk",
  280: "Pacific/Guadalcanal",
  281: "Asia/Magadan",
  284: "Etc/GMT-12",
  285: "Pacific/Fiji",
  290: "Pacific/Auckland",
  295: "Asia/Kamchatka",
  299: "Pacific/Chatham",
  300: "Pacific/Tongatapu",
  301: "America/Cancun",
  302: "Africa/Khartoum",
  303: "America/Punta_Arenas",
  304: "Europe/Volgograd",
  305: "America/Whitehorse"
} as const;

function toDynamicsZonedTime(timezoneCode) {
  const timeZone = dynamicsTzMap[timezoneCode] || "UTC";
  const baseDate = this instanceof Date ? this : new Date(this);
  if (isNaN(baseDate)) return null;

  // Convert to the same moment in the target timezone
  const tzString = baseDate.toLocaleString("en-US", { timeZone });
  const tzDate = new Date(tzString);
  // toLocaleString drops ms; without this the ms leak into the offset and get added twice (23:59:59.999 -> next day).
  tzDate.setMilliseconds(baseDate.getMilliseconds());

  // Figure out offset difference between UTC and that timezone
  const offset = baseDate.getTime() - tzDate.getTime();
  return new Date(baseDate.getTime() + offset);
}

function fromDynamicsZonedTime(timezoneCode) {
  const timeZone = dynamicsTzMap[timezoneCode] || "UTC";
  const baseDate = this instanceof Date ? this : new Date(this);
  if (isNaN(baseDate)) return null;

  // Convert to local representation in the given timezone
  const tzString = baseDate.toLocaleString("en-US", { timeZone });
  return new Date(tzString);
}
interface Date {
  /**
   * Takes a datetime and interprets it AS IF it were in the custom timezone
   * @param timezoneCode - The dynamics timezone code to interpret the datetime in
   * @returns The datetime in the custom timezone
   */
  toDynamicsZonedTime: (timezoneCode: number | string) => Date;
  /**
   * Takes a UTC/custom timezone datetime and interprets it in the local timezone
   * @param timezoneCode - The dynamics timezone code that the datetime is in
   * @returns The datetime in the local timezone
   */
  fromDynamicsZonedTime: (timezoneCode: number | string) => Date;
}

Date.prototype.toDynamicsZonedTime = toDynamicsZonedTime;
Date.prototype.fromDynamicsZonedTime = fromDynamicsZonedTime;